import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

// Components
import { notification } from 'top-component-library';

// Constants
import { TOPLoggedError } from 'constants/Errors';

// Hooks
import { useStores } from 'hooks/use-stores';

// Templates
import Legacy from 'pages/Legacy';
import Map from 'pages/Map';

// Utils
import {
  transformAssetsOldToNew,
  transformCSSVariablesOldToNew,
  transformComponentsOldToNew,
} from 'utils/BOSBuilderPreview';

// Tracking
import { trackLoadBOSLandingPage } from 'integrations/segment/tracking-events/BOSLandingPage';
import { TODO } from 'utils/Types';

const TemplateMap = {
  standard: Legacy,
  map: Map,
  legacy: Legacy,
};

function LandingPage() {
  const history = useHistory();
  const location = useLocation();
  const { hostStore, rootStore } = useStores();

  useEffect(() => {
    if (location.state?.error) {
      notification.error({
        message: location.state.error,
      });

      // Deletes error message from state so the same message doesnt show on page refresh
      const currentState = { ...location.state };
      delete currentState.error;
      history.replace({ ...currentState });
    }
    if (location.state?.joinedTab) {
      notification.success({ message: 'Tab joined successfully!' });
    }
  }, [history, location]);

  // Segment
  useEffect(() => {
    if (hostStore.loaded) {
      trackLoadBOSLandingPage({
        landing_page_template: hostStore.landing_page_template?.document_name,
      });
    }
  }, [hostStore, hostStore.loaded]);

  useEffect(() => {
    window.addEventListener('message', updateChangesFromBOSPreview, false);
  });

  const updateChangesFromBOSPreview = async (event: TODO): Promise<TODO> => {
    const acceptableOriginMap = {
      localdev: 'http://localhost:8000',
      dev: 'https://dev.bbot.menu',
      demo: 'https://demo.bbot.menu',
      staging: 'https://staging.bbot.menu',
      prod: 'https://bbot.menu',
    };

    if (event.origin !== acceptableOriginMap[process.env.REACT_APP_BBOT_ENVIRONMENT]) {
      return;
    }

    if (event.data.cssVariables && event.data.assets && event.data.components && hostStore.loaded) {
      const newCSSVariables = transformCSSVariablesOldToNew(event.data.cssVariables, event.data.fonts);
      const newAssets = transformAssetsOldToNew(event.data.assets);
      const newComponents = transformComponentsOldToNew(event.data.components);
      hostStore.updateHostDataFromBOSBuilder(newCSSVariables, newAssets, newComponents);
    }
    event.stopImmediatePropagation();
  };

  const template = hostStore?.landing_page_template;

  if (!template) {
    return null;
  }

  try {
    const Template = TemplateMap[template.type];
    if (!Template) {
      throw new TOPLoggedError(
        `Template type "${template.type}" is not supported. Please republish your BOS using a supported Tableside Order & Pay template type.`,
        {
          customer_id: rootStore?.hostStore?.host_customer?.customer_id,
          endpoint: 'LandingPage.render',
        }
      );
    }
    return <Template />;
  } catch (err) {
    console.error(err);
    history.push('/error');
    return null;
  }
}

export default observer(LandingPage);
